import { Avatar, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import {
  findAttachment,
  isBuiltinAttached,
  setAttachmentUsage,
} from 'components/@home/@settings/Tabs/Agents/attachedToolsHelpers';
import ChatFlowContextHeader from 'components/@home/@settings/Tabs/ChatFlowContextHeader';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import { AGENT_GLOBAL_BUILTIN_NAMES } from 'constants/agenticToolTriggers';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import FileTreeOutlineIcon from 'mdi-react/FileTreeOutlineIcon';
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon';
import LightningBoltIcon from 'mdi-react/LightningBoltIcon';
import { bool } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { attachedSkillsForAgent } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

const getStyles = theme => {
  const successTone = semanticTone(theme, 'success');
  return {
    root: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      backgroundColor: theme.palette.background.paper,
      flexDirection: 'column',
      overflow: 'hidden',
    },
    body: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    chrome: {
      flexShrink: 0,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    tabContent: {
      flex: '1 1 0',
      minHeight: 0,
      overflowY: 'auto',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(3),
    },
    kindPill: {
      height: 22,
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 600,
      letterSpacing: '0.3px',
      backgroundColor: successTone.bg,
      color: successTone.fg,
      borderRadius: '4px',
      '& .MuiChip-label': { padding: '0 8px' },
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    sectionTitle: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 700,
      color: theme.palette.text.primary,
      letterSpacing: '-0.3px',
    },
    sectionHelper: {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.text.secondary,
      marginTop: 2,
      lineHeight: 1.5,
    },
    skillList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    skillCard: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
      transition: 'background-color 120ms ease, border-color 120ms ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.text.disabled,
      },
    },
    skillIcon: {
      width: 36,
      height: 36,
      borderRadius: '8px',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.secondary,
    },
    skillText: {
      flex: 1,
      minWidth: 0,
    },
    skillName: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    skillUsage: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
      marginTop: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    emptyCard: {
      padding: theme.spacing(3),
      borderRadius: '10px',
      border: `1px dashed ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(13),
      lineHeight: 1.55,
    },
    usageField: {
      maxWidth: 720,
    },
    usageHint: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
    },
    footer: {
      flexShrink: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
    },
  };
};

export function parseBuiltinToolId(toolId) {
  if (!toolId || !String(toolId).startsWith('builtin:')) return null;
  return String(toolId).slice('builtin:'.length);
}

const BuiltinToolEditor = ({ embedded = false }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { agent, flow, authCompany, onSaveAgent, skills } = useAgentEdit();
  const builtin = parseBuiltinToolId(toolId);
  const attachments = agent?.attachedTools || [];
  const attachment = builtin ? findAttachment(attachments, { builtin }) : null;
  const savedUsage = attachment?.usage || '';

  const [draftUsage, setDraftUsage] = useState(savedUsage);
  const [saving, setSaving] = useState(false);
  const lastSavedRef = useRef(savedUsage);

  useEffect(() => {
    setDraftUsage(savedUsage);
    lastSavedRef.current = savedUsage;
  }, [savedUsage, builtin]);

  const attachedSkills = useMemo(
    () => attachedSkillsForAgent(agent, skills || []),
    [agent, skills],
  );

  const skillsWithBuiltin = useMemo(
    () => attachedSkills.filter(skill => isBuiltinAttached(skill.attachedTools, builtin)),
    [attachedSkills, builtin],
  );

  const displayName = builtin
    ? I18n.t(`AgenticChatFlow.BuiltinTools.${builtin}`, { defaultValue: builtin })
    : '';

  const isAnnounce = builtin === 'announce';
  const isAgentGlobalBuiltin = builtin && AGENT_GLOBAL_BUILTIN_NAMES.includes(builtin);
  const isSkillScoped = !attachment && skillsWithBuiltin.length > 0;
  const dirty = Boolean(attachment && draftUsage !== lastSavedRef.current);

  const backToTools = useCallback(() => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'tools' }));
  }, [authCompany, flow?._id, navigate]);

  const openSkillTools = useCallback(
    skillId => {
      if (!flow?._id || !skillId) return;
      navigate(
        buildAgenticPath(authCompany, {
          flowId: flow._id,
          section: 'skills',
          skillId: String(skillId),
        }),
        { state: { skillTab: 'tools' } },
      );
    },
    [authCompany, flow?._id, navigate],
  );

  const identityDescription = useMemo(() => {
    if (isSkillScoped) return I18n.t('AgenticChatFlow.BuiltinSkillScopedLead');
    if (!attachment) return I18n.t('AgenticChatFlow.BuiltinNotAttachedLead');
    if (isAgentGlobalBuiltin) return I18n.t('AgenticChatFlow.AgentGlobalBuiltinEditHint');
    return I18n.t('AgenticChatFlow.BuiltinEditHint');
  }, [attachment, isAgentGlobalBuiltin, isSkillScoped]);

  const breadcrumbs = useMemo(() => {
    if (!builtin || !flow) return null;
    return [
      {
        key: 'home',
        icon: <HomeOutlineIcon size={14} />,
        onClick: backToTools,
        title: I18n.t('AgenticChatFlow.AgentsTitle'),
        ariaLabel: I18n.t('AgenticChatFlow.AgentsTitle'),
      },
      {
        key: 'agent',
        label: flow.name,
        onClick: backToTools,
      },
      {
        key: 'tools',
        label: I18n.t('AgenticChatFlow.NavTools'),
        onClick: backToTools,
      },
      {
        key: 'current',
        label: displayName,
      },
    ];
  }, [backToTools, builtin, displayName, flow]);

  const saveUsage = async () => {
    if (!builtin || !attachment) return;
    const next = draftUsage;
    if (next === lastSavedRef.current) return;
    setSaving(true);
    try {
      const updated = setAttachmentUsage(attachments, { builtin }, next);
      await onSaveAgent({ attachedTools: updated });
      lastSavedRef.current = next;
    } finally {
      setSaving(false);
    }
  };

  if (!builtin) return null;

  const builtinIcon = isAnnounce ? <LightningBoltIcon size={22} /> : <CodeTagsIcon size={22} />;

  return (
    <Box sx={[styles.root, embedded && { flex: '1 1 0', minHeight: 0, borderLeft: 'none' }]}>
      {!embedded && breadcrumbs && <ChatFlowContextHeader breadcrumbs={breadcrumbs} />}
      <Box component="section" sx={styles.body}>
        <Box sx={styles.chrome}>
          <IdentityCard
            icon={builtinIcon}
            title={displayName}
            slug={builtin}
            pill={
              <Chip
                size="small"
                icon={<CodeTagsIcon size={14} />}
                sx={styles.kindPill}
                label={I18n.t('AgenticChatFlow.BuiltinTool')}
              />
            }
            description={identityDescription}
            divider
          />
        </Box>

        <Box sx={styles.tabContent}>
          {isSkillScoped && (
            <Box>
              <Box sx={styles.sectionHeader}>
                <Box>
                  <Typography sx={styles.sectionTitle}>
                    {I18n.t('AgenticChatFlow.Skills')}
                  </Typography>
                  <Typography sx={styles.sectionHelper}>
                    {I18n.t('AgenticChatFlow.BuiltinSkillScopedSectionHelper')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.skillList}>
                {skillsWithBuiltin.map(skill => {
                  const att = findAttachment(skill.attachedTools, { builtin });
                  const usage = (att?.usage || '').trim();
                  return (
                    <Box
                      key={skill._id}
                      sx={styles.skillCard}
                      onClick={() => openSkillTools(skill._id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openSkillTools(skill._id);
                        }
                      }}
                    >
                      <Avatar variant="rounded" sx={styles.skillIcon}>
                        <FileTreeOutlineIcon size={18} />
                      </Avatar>
                      <Box sx={styles.skillText}>
                        <Typography sx={styles.skillName}>
                          {skill.displayName || skill.name}
                        </Typography>
                        {usage ? (
                          <Typography sx={styles.skillUsage} title={usage}>
                            {usage}
                          </Typography>
                        ) : (
                          <Typography sx={styles.skillUsage}>
                            {I18n.t('AgenticChatFlow.NoUsageConfigured')}
                          </Typography>
                        )}
                      </Box>
                      <ChevronRightIcon size={20} color="inherit" style={{ opacity: 0.45 }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {!attachment && !isSkillScoped && (
            <Box sx={styles.emptyCard}>{I18n.t('AgenticChatFlow.BuiltinNotAttached')}</Box>
          )}

          {attachment && (
            <Stack spacing={1.5} sx={styles.usageField}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {I18n.t('AgenticChatFlow.UsageLabel')}
              </Typography>
              <TextField
                value={draftUsage}
                onChange={e => setDraftUsage(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={isAnnounce ? 3 : 2}
                placeholder={
                  isAnnounce
                    ? I18n.t('AgenticChatFlow.AnnounceUsagePlaceholder')
                    : I18n.t('AgenticChatFlow.UsagePlaceholder')
                }
              />
              {isAnnounce && (
                <Typography sx={styles.usageHint}>
                  {I18n.t('AgenticChatFlow.AnnounceUsageHint')}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {attachment && (
        <Box component="footer" sx={styles.footer}>
          <Button
            variant="contained"
            color="primary"
            onClick={saveUsage}
            disabled={saving || !dirty}
            disableElevation
            sx={{ textTransform: 'none', borderRadius: '5px' }}
          >
            {I18n.t('AgenticChatFlow.Save')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

BuiltinToolEditor.propTypes = {
  embedded: bool,
};

export default BuiltinToolEditor;
